import type {
    Revision,
    RevisionFileMetadata,
    RevisionMetadata,
    RevisionPageMetadata,
    RevisionReusableContentMetadata,
} from '../../types';
import { mutations } from '../../utils';

export type RevisionMetadataUpdate = Partial<RevisionMetadata> & {
    pages?: {
        [pageId: string]: Partial<RevisionPageMetadata>;
    };
    files?: {
        [fileId: string]: Partial<RevisionFileMetadata>;
    };
    reusableContents?: {
        [reusableContentId: string]: Partial<RevisionReusableContentMetadata>;
    };
};

/**
 * Update metadata on a revision.
 * This function can be used on a an existing revision to mutate it.
 */
export function updateRevisionMetadata(
    revision: mutations.MutableData<Revision>,
    update: RevisionMetadataUpdate
) {
    if (update.git) {
        mutations.set(revision, 'git', update.git);
    }

    if (update.pages) {
        for (const [pageId, pageUpdate] of Object.entries(update.pages)) {
            const page = mutations.getIn(revision, ['pages', pageId] as const);
            if (!page) {
                throw new Error(`Page ${pageId} does not exist in revision`);
            }

            if (pageUpdate.git) {
                mutations.set(page, 'git', pageUpdate.git);
            }
        }
    }

    if (update.files) {
        for (const [fileId, fileUpdate] of Object.entries(update.files)) {
            const file = mutations.getIn(revision, ['files', fileId] as const);
            if (!file) {
                throw new Error(`File ${fileId} does not exist in revision`);
            }

            if (fileUpdate.git) {
                mutations.set(file, 'git', fileUpdate.git);
            }
        }
    }

    if (update.reusableContents) {
        for (const [reusableContentId, reusableContentUpdate] of Object.entries(
            update.reusableContents
        )) {
            const reusableContent = mutations.getIn(revision, [
                'reusableContents',
                reusableContentId,
            ] as const);
            if (!reusableContent) {
                throw new Error(`Reusable content ${reusableContentId} does not exist in revision`);
            }

            if (reusableContentUpdate.git) {
                mutations.set(reusableContent, 'git', reusableContentUpdate.git);
            }
        }
    }
}
